import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, MapPin, Mail, Phone } from "lucide-react";
import { useSiteContent } from "../context/ContentContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import ScrollToTopButton from "./ScrollToTopButton";
import CookieConsent from "./CookieConsent";
import ProtectedEmailLink from "./ProtectedEmailLink";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/quality", label: "Quality" },
  { to: "/compliance", label: "Compliance" },
  { to: "/contact", label: "Contact Us" },
];

function Logo() {
  const { content } = useSiteContent();
  const { settings, resolveAssetUrl } = useSiteSettings();
  const c = content?.company || {};
  const headerLogo = settings?.visual?.logos?.header || {};
  const logoSrc = resolveAssetUrl(headerLogo.url);
  const alignMap = { left: "justify-start", center: "justify-center", right: "justify-end" };
  return (
    <Link to="/" className={`flex items-center gap-3 min-w-0 ${alignMap[headerLogo.align] || alignMap.left}`} data-testid="logo-home-link">
      {logoSrc ? (
        <img
          src={logoSrc}
          alt={c.name || settings?.seo?.company_name || "Vissera"}
          style={{ maxHeight: `${headerLogo.max_height || 44}px`, objectFit: headerLogo.object_fit || "contain" }}
          className="w-auto max-w-[220px] shrink-0"
        />
      ) : (
        <>
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[var(--gold-hover)] to-[var(--gold)] flex items-center justify-center shadow-md shrink-0">
            <span className="text-white font-display font-bold text-lg sm:text-xl">X</span>
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-display font-bold text-sm sm:text-base tracking-tight text-[var(--text-primary)]">{c.logo_text_1 || "VISSERA"}</div>
            <div className="text-[10px] tracking-[0.22em] text-[var(--gold)] font-semibold">{c.logo_text_2 || "PRECIOUS METALS"}</div>
          </div>
        </>
      )}
    </Link>
  );
}

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }}>
      {/* Floating pill navigation */}
      <header
        className={`fixed left-1/2 -translate-x-1/2 w-[calc(100%-1rem)] sm:w-[calc(100%-1.5rem)] md:w-[95%] max-w-[1320px] z-40 transition-all duration-300 ${
          scrolled ? "top-2 md:top-3" : "top-3 md:top-6"
        }`}
      >
        <nav
          className={`rounded-full backdrop-blur-xl border flex items-center justify-between px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3 gap-3 ${
            scrolled ? "shadow-[0_18px_40px_-20px_rgba(0,0,0,0.18)]" : "shadow-sm"
          }`}
          style={{ backgroundColor: "rgba(255,255,255,0.9)", borderColor: "var(--border-light)" }}
          data-testid="main-nav"
        >
          <Logo />
          <div className="hidden xl:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/contact" className="btn-primary" data-testid="nav-cta-quote">
              Request a Quote
            </Link>
            <button
              className="xl:hidden p-2 rounded-full border theme-text-primary"
              style={{ borderColor: "var(--border-light)" }}
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="xl:hidden mt-3 rounded-3xl theme-surface shadow-xl p-4 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-2xl text-sm font-semibold tracking-wide uppercase ${
                      isActive ? "theme-bg-secondary theme-text-accent" : "theme-text-primary hover:bg-[var(--bg-secondary)]"
                    }`
                  }
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="btn-primary mt-3 justify-center" data-testid="mobile-nav-cta">
                Request a Quote
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* spacer for fixed header */}
      <div className="h-24 sm:h-28" />

      <main className="flex-1" data-testid="main-content">{children}</main>

      <Footer />
      <CookieConsent />
      <ScrollToTopButton />
    </div>
  );
}

function Footer() {
  const { content } = useSiteContent();
  const { settings, resolveAssetUrl } = useSiteSettings();
  const c = content?.company || {};
  const footerLogo = settings?.visual?.logos?.footer || {};
  const footerLogoSrc = resolveAssetUrl(footerLogo.url);
  return (
    <footer className="theme-bg-dark text-[var(--interactive-text)] pt-20 pb-10" data-testid="site-footer">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6 min-h-[52px]">
              {footerLogoSrc ? (
                <img
                  src={footerLogoSrc}
                  alt={`${c.name || settings?.seo?.company_name || "Vissera"} footer logo`}
                  style={{ maxHeight: `${footerLogo.max_height || 52}px`, objectFit: footerLogo.object_fit || "contain" }}
                  className="w-auto max-w-[240px]"
                />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--gold-hover)] to-[var(--gold)] flex items-center justify-center">
                    <span className="text-white font-display font-bold text-xl">X</span>
                  </div>
                  <div>
                    <div className="font-display font-bold text-base">{c.logo_text_1 || "VISSERA"}</div>
                    <div className="text-[10px] tracking-[0.22em] text-[var(--gold)] font-semibold">{c.logo_text_2 || "PRECIOUS METALS"}</div>
                  </div>
                </>
              )}
            </div>
            <p className="theme-panel-dark-muted text-sm leading-relaxed">
              {c.short || "Istanbul-based import and export of investment-grade gold and silver. Trusted purity, transparent pricing, secure global delivery."}
            </p>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold mb-5">EXPLORE</div>
            <ul className="space-y-3 text-sm theme-panel-dark-muted">
              {NAV.slice(0, 4).map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-[var(--gold-hover)] transition-colors">{n.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold mb-5">COMPANY</div>
            <ul className="space-y-3 text-sm theme-panel-dark-muted">
              {NAV.slice(4).map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-[var(--gold-hover)] transition-colors">{n.label}</Link></li>
              ))}
              <li><Link to="/admin" className="hover:text-[var(--gold-hover)] transition-colors">Admin</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold mb-5">CONTACT</div>
            <ul className="space-y-3 text-sm theme-panel-dark-muted">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 theme-text-accent mt-0.5 shrink-0" /> {c.address}</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 theme-text-accent shrink-0" /> {c.phone}</li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 theme-text-accent shrink-0" />
                <ProtectedEmailLink className="hover:text-[var(--gold-hover)] transition-colors" testid="footer-email" />
              </li>
              <li className="theme-panel-dark-subtle text-xs pt-1">{c.hours}</li>
            </ul>
          </div>
        </div>

        <div className="border-t pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs theme-panel-dark-subtle" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <div>© {new Date().getFullYear()} {c.name || "Vissera"}. All rights reserved.</div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="hover:text-[var(--interactive-text)] transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-[var(--interactive-text)] transition-colors">Terms & Conditions</Link>
            <Link to="/cookie-policy" className="hover:text-[var(--interactive-text)] transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
